import { useState, useEffect } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * ConfirmDialog: promise-based replacement for window.confirm using shadcn AlertDialog.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({title, description, confirmLabel, variant: "danger"});
 *
 * Variants: "danger" (red button), "default" (yellow POPCRN pink).
 */
export function ConfirmDialogHost({ apiRef }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    apiRef.current = ({ title, description, confirmLabel = "Bestätigen",
                       cancelLabel = "Abbrechen", variant = "default",
                       details = null }) =>
      new Promise((resolve) => {
        setState({ title, description, confirmLabel, cancelLabel, variant, details, resolve });
      });
  }, [apiRef]);

  if (!state) return null;
  const close = (ok) => { state.resolve(ok); setState(null); };

  const confirmCls = state.variant === "danger"
    ? "bg-red-600 hover:bg-red-700 text-white font-bold rounded-full"
    : "bg-[var(--pop-yellow)] hover:bg-[var(--pop-yellow-dark)] text-black font-bold rounded-full";

  return (
    <AlertDialog open onOpenChange={(v) => { if (!v) close(false); }}>
      <AlertDialogContent data-testid="confirm-dialog" className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-2xl">{state.title}</AlertDialogTitle>
          {state.description && (
            <AlertDialogDescription className="text-sm text-[var(--text-muted)] whitespace-pre-line">
              {state.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {state.details && (
          <div className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] p-3"
               data-testid="confirm-details">
            <ul className="space-y-1 text-xs font-mono">
              {state.details.map((d, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span>{d.left}</span>
                  <span className="text-[var(--text-muted)]">{d.right}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="confirm-cancel">
            {state.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction className={confirmCls} data-testid="confirm-ok" onClick={() => close(true)}>
            {state.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
